/**
 * Le tiroir « Traitements » : la file, rendue lisible au métreur.
 *
 * C'est la machine à états de l'import mise à l'écran — `M9`. Ce qu'elle
 * montre est ce que le portail dit ; ni le propriétaire du bail ni son
 * échéance n'y figurent : le nom d'un processus n'apprend rien à un métreur.
 *
 * **La relance s'arrête quand l'onglet passe en arrière-plan.** Un tiroir
 * laissé ouvert une nuit interrogerait le portail dix mille fois pour
 * personne.
 */
import { api, ErreurApi } from "../../api/client";
import type { Travail } from "../../api/types";
import { Chargement, Echec, Vide } from "../../composants/etats";
import { Etat } from "../../composants/etiquettes";
import { quand } from "../../composants/formats";
import { TRAVAIL_EN_COURS, type Veille } from "../../composants/donnees";

/** **Le tiroir ne va plus chercher la file lui-même.** Il la recevait déjà
 *  d'un point d'accès que la page interroge en continu ; deux relances sur la
 *  même adresse doublaient les requêtes pour la même réponse. */
export function Traitements({
  veille, fermer,
}: {
  veille: Veille<Travail>;
  fermer: () => void;
}) {
  const liste = veille.travaux;

  async function relancer(travail: Travail) {
    try {
      await api.ecrire(`/api/v1/traitements/${travail.job_id}/relance`);
    } catch (exc) {
      if (!(exc instanceof ErreurApi)) throw exc;
    }
    veille.rafraichir();
  }

  return (
    <aside className="tiroir" aria-label="Traitements">
      <div className="carte__tete">
        <h3 style={{ flex: 1 }}>Traitements</h3>
        {veille.enCours > 0 && <span className="etiquette etiquette--attente">en cours</span>}
        <button type="button" className="bouton bouton--discret" onClick={fermer}>
          Fermer
        </button>
      </div>

      <div className="tiroir__corps">
        {veille.charge && veille.travaux.length === 0 ? (
          <Chargement lignes={3} />
        ) : veille.erreur ? (
          <Echec erreur={veille.erreur} reessayer={veille.rafraichir} />
        ) : liste.length === 0 ? (
          <Vide
            titre="Rien en cours"
            aide="Les imports et les préparations apparaîtront ici."
          />
        ) : (
          <div className="pile">
            {liste.map((travail) => (
              <div key={travail.job_id} className="carte">
                <div className="carte__corps pile pile--serree">
                  <div className="rangee rangee--espacee">
                    <strong style={{ fontSize: "var(--mo-text-base)" }}>
                      {travail.nom ?? travail.genre}
                    </strong>
                    <Etat genre="travail" valeur={travail.etat} />
                  </div>

                  {TRAVAIL_EN_COURS.has(travail.etat) && (
                    <div className="jauge" aria-label={`${travail.progression} %`}>
                      <div className="jauge__part" style={{ width: `${travail.progression}%` }} />
                    </div>
                  )}

                  <span className="faible nombre">{quand(travail.modifie_le)}</span>

                  {travail.motif && (
                    <div className="avis avis--erreur" style={{ fontSize: "var(--mo-text-sm)" }}>
                      <div className="pile pile--serree" style={{ flex: 1 }}>
                        <strong>{travail.motif}</strong>
                        {travail.detail && <span>{travail.detail}</span>}
                      </div>
                    </div>
                  )}

                  {travail.etat === "FAILED" && (
                    <button
                      type="button"
                      className="bouton"
                      onClick={() => relancer(travail)}
                    >
                      Relancer
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
